// =========================================================================
// FORME EMPIRIQUE DE LA PROP SCROLL (O-1) — figee par inspection reelle du
// `data-page` rendu par /blog (inertia_rails 3.21.1, KaminariAdapter), pas par
// supposition. Inspection : requete Inertia JSON sur /blog?page=1 avec 7
// articles publies (fixtures de test). Resultat observe :
//
//   page.props.articles    -> TABLEAU NU d'ArticleCardProps[] (6 par lot ;
//                             le serveur fusionne en APPEND automatiquement via
//                             InertiaRails.scroll — JAMAIS de wrapper { data }).
//   page.props.total_count -> Integer (7) : compteur total "{n} sur {total}".
//   page.scrollProps.articles -> { pageName: "page", previousPage: number|null,
//                             nextPage: number|null, currentPage: number,
//                             reset: boolean }  (cles camelCase).
//
// Les metadonnees de page (nextPage/currentPage/pageName) vivent donc dans
// page.scrollProps.articles (cle = nom de la prop scroll), PAS dans props.
// Le type client `Page` de @inertiajs/react 2.1.10 N'EXPOSE PAS `scrollProps`
// (verifie dans @inertiajs/core/types/types.d.ts) -> on lit cette cle via un
// type local etendu sur le retour de usePage() (cast type, conforme a la forme
// observee). pageName === "page" => le lien crawlable ?page=N (D-06) cible bien
// le parametre serveur.
// =========================================================================
import type { MouseEvent } from "react";
import { Head, Link, WhenVisible, router, usePage } from "@inertiajs/react";
import { Calendar, ImageOff } from "lucide-react";
import { Button } from "../../components/ui";

// Forme serialisee par ArticleSerializer (vue :card).
type ArticleCardProps = {
  title: string;
  slug: string;
  tags: string[];
  meta_description: string | null;
  featured_image_url: string | null;
  published_at: string;
};

type BlogIndexProps = {
  // Tableau nu fusionne en append par InertiaRails.scroll (cf. en-tete O-1).
  articles: ArticleCardProps[];
  // Compteur "{n} sur {total}" (D-05).
  total_count: number;
};

// Metadonnees scroll injectees par le serveur dans page.scrollProps.<nom-prop>.
// Non typees par le client Inertia 2.1.10 -> type local pour un acces sur.
type ScrollMetadata = {
  pageName: string;
  previousPage: number | null;
  nextPage: number | null;
  currentPage: number;
  reset: boolean;
};

const formatDate = (iso: string) => {
  // Format FR jour mois annee (rendu deterministe pour le SSR).
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return "";
  }
};

// Carte article — traduction fidele de BlogCard (maquette blog.jsx:146-168).
// Le wrapper est un <Link> crawlable (et non un onClick onOpen du prototype).
const ArticleCard = ({ article }: { article: ArticleCardProps }) => (
  <Link
    href={`/blog/articles/${article.slug}`}
    className="group bg-white rounded-xl border border-gray-100 shadow-xs overflow-hidden hover:shadow-md transition-shadow flex flex-col no-underline text-inherit"
  >
    {article.featured_image_url ? (
      <img
        src={article.featured_image_url}
        alt={article.title}
        loading="lazy"
        className="w-full h-[180px] object-cover block bg-gray-100"
      />
    ) : (
      <div
        className="w-full h-[180px] flex items-center justify-center bg-gray-100 text-gray-400 select-none"
        aria-hidden="true"
      >
        <ImageOff className="w-9 h-9" strokeWidth={1.5} />
      </div>
    )}
    <div className="p-5 flex flex-col gap-3 flex-1">
      {article.tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {article.tags.map((tag) => (
            // Chips affiches mais NON cliquables (D-07 : plus de navigation ?tag=).
            <span
              key={tag}
              className="bg-[#EFECFF] text-[#412FB2] rounded-md px-2.5 py-1 text-xs font-medium"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
      <h3 className="text-lg font-bold leading-snug group-hover:underline text-balance">
        {article.title}
      </h3>
      {article.meta_description && (
        <p className="text-sm text-muted-foreground leading-6 line-clamp-3">
          {article.meta_description}
        </p>
      )}
      <div className="mt-auto pt-1 flex items-center gap-2 text-sm text-muted-foreground">
        <Calendar className="w-4 h-4" />
        <span>{formatDate(article.published_at)}</span>
      </div>
    </div>
  </Link>
);

const BlogIndex = ({ articles, total_count }: BlogIndexProps) => {
  const hasArticles = articles.length > 0;

  // Metadonnees scroll (nextPage) lues depuis page.scrollProps.articles.
  const page = usePage();
  const scrollMeta = (
    page as unknown as { scrollProps?: Record<string, ScrollMetadata> }
  ).scrollProps?.articles;
  const nextPage = scrollMeta?.nextPage ?? null;

  const shown = articles.length;

  // Interception du clic : reload partiel (append) au lieu d'une navigation
  // pleine. Le serveur fusionne le lot suivant dans `articles` (D-05).
  const loadMore = (event: MouseEvent<HTMLAnchorElement>) => {
    if (nextPage == null) return;
    event.preventDefault();
    router.reload({ only: ["articles"], data: { page: nextPage } });
  };

  return (
    // Container aligné sur celui du header (Layout/components/Header.tsx :
    // `container mx-auto`) : largeurs max responsives Tailwind (jusqu'à 1536px en
    // 2xl), centré, avec le même padding horizontal `px-4` que le `p-4` du header.
    // Le contenu de la liste s'aligne ainsi exactement sous la barre de navigation.
    <div className="container mx-auto px-4 my-8 lg:my-12">
      <Head title="Le blog - Review Car" />

      <div className="mb-8 lg:mb-10">
        <h1 className="text-3xl lg:text-4xl font-bold">Le blog Review Car</h1>
        <p className="text-muted-foreground mt-2 max-w-[640px]">
          Analyses, comparatifs et tendances du marché automobile, à partir des
          avis certifiés de vrais conducteurs.
        </p>
      </div>

      {hasArticles ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => (
              <ArticleCard key={article.slug} article={article} />
            ))}
          </div>

          {nextPage != null && (
            // Scroll infini (D-05) : WhenVisible declenche automatiquement le
            // reload partiel (append) des que le bloc approche du viewport
            // (params). Le `fallback` est l'affichage de base (bouton + compteur)
            // : le <a href="?page=N"> reste un vrai lien crawlable (D-06), donc
            // le fallback sans JS est preserve. Les `children` ne s'affichent que
            // pendant le chargement du lot suivant.
            <WhenVisible
              always
              buffer={240}
              params={{ only: ["articles"], data: { page: nextPage } }}
              fallback={
                <div className="flex flex-col items-center gap-3 mt-10">
                  {/* asChild : le <a> EST le bouton (pas d'interactifs imbriques),
                      href ?page=N reste crawlable (D-06). */}
                  <Button asChild variant="outline" size="lg">
                    <a href={`/blog?page=${nextPage}`} onClick={loadMore}>
                      Voir plus d'articles
                    </a>
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    {shown} sur {total_count}
                  </span>
                </div>
              }
            >
              <div className="flex flex-col items-center gap-3 mt-10 text-xs text-muted-foreground">
                Chargement…
              </div>
            </WhenVisible>
          )}
        </>
      ) : (
        // Etat vide : aucun article publie.
        <div className="text-center py-12">
          <h2 className="text-lg font-bold">Aucun article pour le moment</h2>
          <p className="text-muted-foreground mt-2">
            Nos premiers articles arrivent bientôt. En attendant, explorez les
            avis sur les véhicules.
          </p>
          <Button className="mt-4" onClick={() => router.visit("/vehicles")}>
            Rechercher un véhicule
          </Button>
        </div>
      )}
    </div>
  );
};

export default BlogIndex;
